import { computed, effect, inject, signal, Service } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import { first } from 'rxjs';
import { GroupStore } from '@thiememeulenhoff/ng-store';
import { Sort } from '@angular/material/sort';
import { ManagementContentService } from './management-content.service';
import { ArrangementService } from './arrangement.service';

@Service()
export class ResultsService {
  readonly #groupStore = inject(GroupStore);

  readonly #arrangementService = inject(ArrangementService);

  readonly #contentService = inject(ManagementContentService);

  readonly #arrangementCode = signal('');

  readonly #contentIdentifier = signal('');

  readonly errorMessage = signal<string | null>(null);

  readonly errorStatus = signal<number | null>(null);

  readonly selectedGroupId = this.#groupStore.validatedSelectedGroupId;

  readonly #sort = signal<Sort>({ active: 'user-name', direction: '' });

  readonly sort = this.#sort.asReadonly();

  readonly #isArrangementLoading = signal(false);

  readonly #isResourcesLoading = signal(false);

  readonly isLoading = computed(() => {
    return this.#isArrangementLoading() || this.#isResourcesLoading();
  });

  readonly resource = rxResource({
    params: () => this.#contentIdentifier(),
    stream: ({ params: id }) => {
      return this.#contentService.resourceById$(id);
    },
  });

  setIdentifiers(arrangementCode: string, contentIdentifier: string) {
    this.#arrangementCode.set(arrangementCode);
    this.#contentIdentifier.set(contentIdentifier);
    this.errorMessage.set(null);
    this.errorStatus.set(null);
  }

  constructor() {
    this.#fetchArrangement();
    this.#fetchResources();
  }

  #fetchArrangement() {
    effect(() => {
      const arrangementCode = this.#arrangementCode();
      if (arrangementCode) {
        this.#isArrangementLoading.set(true);
        this.#arrangementService
          .fetchArrangementWithSources(arrangementCode)
          .pipe(first())
          .subscribe({
            error: (error: unknown) => {
              this.#setErrorMessage(error, 'Kon de streamgegevens niet laden.');
              this.#isArrangementLoading.set(false);
            },
            complete: () => {
              this.#isArrangementLoading.set(false);
            },
          });
      }
    });
  }

  #fetchResources() {
    effect(() => {
      const arrangementCode = this.#arrangementCode();
      const contentIdentifier = this.#contentIdentifier();
      if (arrangementCode && contentIdentifier) {
        this.#isResourcesLoading.set(true);
        this.#contentService
          .fetchResources(arrangementCode, contentIdentifier)
          .pipe(first())
          .subscribe({
            error: (error: unknown) => {
              this.#setErrorMessage(error, 'Kon de resources niet laden.');
              this.#isResourcesLoading.set(false);
            },
            complete: () => {
              this.#isResourcesLoading.set(false);
            },
          });
      }
    });
  }

  #setErrorMessage(error: unknown, fallback: string) {
    if (error instanceof HttpErrorResponse) {
      this.errorMessage.set(`${fallback} (status ${error.status}).`);
      this.errorStatus.set(error.status);
      return;
    }

    this.errorMessage.set(fallback);
    this.errorStatus.set(500);
  }
}
